import React, { useCallback, useEffect, useRef} from 'react';
import {set, get} from 'immutadot';
import isEqual from 'react-fast-compare';
import {extractCreationIds, applyDiffs, applyCreationIds, diffObjects, purgeCreationIds} from './id-diff-merge';
import {deepCopy} from "../utils";
import { FieldMap } from "../utils/tree-walker";

export type DiaFormRelationships = FieldMap;


function convertToObjectPath(path, values, prefix = '', returnValue = {
    path: '',
    exists: false,
    parent: null,
    arrayIndex: -1,
    isArray: false
}) {
    // takes (id).property and converts to [idx].property
    if (!path && path !== '0') {
        returnValue.path = prefix;
        returnValue.exists = true;
        return returnValue;
    }
    if (!values) {
        returnValue.path = prefix + '.' + path;
        returnValue.exists = true;
        return returnValue;
    }
    const parts = path.split('.');
    const currentPart = parts.shift();
    if (currentPart.indexOf('(') > -1) {
        const divided = currentPart.split('(');
        const name = divided[0];
        const key = divided[1].slice(0, -1);
        let value = null;
        prefix = (prefix ? prefix + '.' + name : name);
        returnValue.parent = prefix;
        returnValue.isArray = true;
        values[name].forEach((v, idx) => {
            if (value) return;
            if (v.id == key || v.creation_id == key) {
                value = convertToObjectPath(parts.join('.'), v, prefix + '[' + idx + ']', returnValue);
                value.arrayIndex = idx;
            }
        });
        return value ? value : returnValue;
    }

    if (parts.length < 1 || parts[0] == '') {
        return {path: prefix + '.' + currentPart};
    }
    return convertToObjectPath(parts.join('.'), values[currentPart], prefix + '.' + currentPart);
}

export enum DiaFormActions {
    UPDATE,
    SUBMIT,
    INITIALIZE,
    STATUS_SET,
    VALUE_SET,
    ERRORS_SET,
    TRANSIENT_SET,
    VERIFY_DIRTY
}


type DiaFormInitialFunction<Type> = (state: DiaFormState<Type, any>, rawValues: Type) => DiaFormState<Type, any>;

export type DiaFormErrors = {
    [key: string]: string
}

type DiaFormState<Type,TransientType=never> = {
    status: 'ready' | 'loading' | 'validating' | 'submitting' | 'submit-failed',
    isDirty: boolean,
    isNew: boolean,
    initialValues: Type|null,
    transientValues?: TransientType,
    relationships?: DiaFormRelationships,
    writeSeq: number,
    values: Type,
    errors?: DiaFormErrors,
}

export interface DiaFormSubmitCallbackProps<Type> {
    values: Type,
    initialValues: Type,
    hasFailed?: boolean,
    setTransient:(name:string,value:string)=>void,
    reInitialize?: (newValues: Type, rawInputValues: Type, isNew: boolean) => void
}

export type DiaFormAction<Type> =
    DiaFormActionSet<Type>
    | DiaFormActionStatus<Type>
    | DiaFormActionSubmit<Type>
    | DiaFormActionInitialize<Type>
    | DiaFormActionUpdate<Type>
    | DiaFormActionErrorsSet<Type>
    | DiaFormActionVerifyDirty<Type>;

interface DiaFormActionSet<Type> {
    type: DiaFormActions.VALUE_SET|DiaFormActions.TRANSIENT_SET,
    name: string,
    value: any
}



interface DiaFormActionErrorsSet<Type> {
    type: DiaFormActions.ERRORS_SET,
    value: any
}
interface DiaFormActionVerifyDirty<Type> {
    type: DiaFormActions.VERIFY_DIRTY
}


interface DiaFormActionStatus<Type> {
    type: DiaFormActions.STATUS_SET,
    value: 'submitting' | 'loading' | 'validating' | 'ready' | 'submit-failed'
}


interface DiaFormActionUpdate<Type> {
    type: DiaFormActions.UPDATE,
    values: Type,
    name: string
}

interface DiaFormActionSubmit<Type> {
    type: DiaFormActions.SUBMIT,
    onSubmit: (values: Type) => Type,
    validationSchema?: YupSchema
}

interface DiaFormActionInitialize<Type> {
    type: DiaFormActions.INITIALIZE,
    initializer?: DiaFormInitialFunction<Type>,
    relationships?: DiaFormRelationships,
    inputValues?: Type,
    rawValues?: any,
    isNew?: boolean,
    transientValues?: any,
}

function isFunction<Type>(x: any): x is DiaFormInitialFunction<Type> {
    return typeof x === "function";
}

function extractChangeableObject(src,relations,currentPath=null) {
    // to objects 'connected', the only interesting change is to the ID of the object itself
    // so create a copy of the object without all the other properties and return that
    // - pre-processing the relationship list could make this a lot faster
    // - not fetching the lookup table values in the .values graphql query would avoid the need for this
    if(!relations) return src;
    if(Array.isArray(src)) {
        const output:any[] = [];
        src.forEach((innerSrc)=>{
            output.push(extractChangeableObject(innerSrc,relations,currentPath));
        });
        return output;
    }
    if(src && typeof src == 'object') {
        if(relations[currentPath??'']=='connect') {
            return {
                id:src.id
            };
        } else {
            const returnObject = {};
            Object.entries(src).forEach(([key,value])=>{
                returnObject[key]  = extractChangeableObject(value,relations,currentPath?currentPath+'.'+key:key);
            });
            return returnObject;
        }
    }
    return src;
}

function computeDirty<Type,TransientType>(state: DiaFormState<Type,TransientType>): boolean {
    // relationship-aware comparison: 'connect' subtrees collapse to their id,
    // and transient (derived) fields are stripped so they never count as a
    // change — they can exist on `values` without a matching `initialValues`
    // key (e.g. the rigging date/time split of a wire DateTime).
    const transient = transientFields(state.relationships);
    return !isEqual(
        extractChangeableObject(stripTransient(state.initialValues, transient), state.relationships),
        extractChangeableObject(stripTransient(state.values, transient), state.relationships)
    );
}

// names of the top-level write-only fields declared in the relationships map
// (a write-only field's read value is blank; the form resets it after a
// successful save so the form does not stay dirty).
function writeOnlyFields(relationships?: DiaFormRelationships): string[] {
    if (!relationships) return [];
    return Object.entries(relationships)
        .filter(([, v]) => v === 'writeOnly')
        .map(([k]) => k.split('.').pop() as string);
}

// names of the transient fields declared in the relationships map. Transient
// fields are derived / write-only convenience values (e.g. the rigging
// date+time split of a single wire DateTime) that are stripped from the
// payload by the diff engine and must NOT count toward the dirty check: they
// can legitimately exist on `values` without a matching key on `initialValues`,
// so including them in the comparison would make the form flap dirty.
function transientFields(relationships?: DiaFormRelationships): string[] {
    if (!relationships) return [];
    return Object.entries(relationships)
        .filter(([, v]) => v === 'transient')
        .map(([k]) => k.split('.').pop() as string);
}

// Strip the transient fields from an object so the dirty comparison ignores
// them. Only top-level (leaf) transient names are removed; nested transient
// paths (e.g. `areaBookings.total_value`) are addressed by their leaf name,
// so a shallow strip is sufficient for the relationship-aware comparison.
function stripTransient(obj: any, names: string[]): any {
    if (!obj || typeof obj != 'object' || !names.length) return obj;
    if (Array.isArray(obj)) return obj;
    const out: any = {};
    Object.keys(obj).forEach((k) => {
        if (names.indexOf(k) !== -1) return;
        out[k] = obj[k];
    });
    return out;
}


export function reducer<Type,TransientType>(state: DiaFormState<Type,TransientType>, action: DiaFormAction<Type>): DiaFormState<Type,TransientType> {
    let newState: DiaFormState<Type,TransientType>;
    const getInitialValues = () => {
        return (action as DiaFormActionUpdate<Type>).name ? get(state.initialValues, (action as DiaFormActionUpdate<Type>).name) : state.initialValues;
    }
    const getCurrentValues = () => {
        return (action as DiaFormActionUpdate<Type>).name ? get(state.values, (action as DiaFormActionUpdate<Type>).name) : state.values;
    }
    switch (action.type) {
        case DiaFormActions.UPDATE:
            newState = state;
            // this is used to synchronize with a remote data source
            const initialValues = getInitialValues();
            const currentValues = getCurrentValues();
            let newValues: unknown = action.values;
// if creation_id is given for objects in the response, remove them and apply them to the initialValues and currentvalues
            const cIds = extractCreationIds(newValues);
            newValues = purgeCreationIds(newValues, cIds);
            const original = applyCreationIds(initialValues, cIds);
            const local = applyCreationIds(currentValues, cIds);

            // find all local changes that haven't been committed to the server yet
            const diffs = diffObjects(original, local);
            // apply those local changes to the values received from the server, and use as the new local state
            const newResult = applyDiffs(newValues, diffs);
            // set the server state to the value received from the server (with the creation_ids removed)
            newState = set(newState, 'initialValues' + (action.name ? '.' + action.name : ''),deepCopy(newValues));
            // set the local state to the merged values from above
            newState.status = 'ready';
            newState = set(newState, 'values' + (action.name ? '.' + action.name : ''), deepCopy(newResult));
            // remote data is a verification point: run the relationship-aware comparison
            // now (a rare event) and clear the pending-write marker
            newState.isDirty = computeDirty(newState);
            newState.writeSeq = 0;
            return newState;
        case DiaFormActions.SUBMIT:
            newState = state;
            if (action.validationSchema) {
                newState = set(newState, 'status', 'validating');
            } else {
                newState = set(newState, 'status', 'submitting');
                //                action.onSubmit({values:state.values,initialValues:state.initialValues});
            }
            return newState;
        case DiaFormActions.INITIALIZE:
            if (isFunction(action.initializer)) {
                newState = action.initializer(state, action.inputValues);
            } else {
                newState = {
                    initialValues: deepCopy(action.inputValues),
                    isDirty: false,
                    isNew: false,
                    status: undefined,
                    relationships:action.relationships,
                    values: deepCopy(action.inputValues),
                    transientValues:action.transientValues,
                    writeSeq: 0
                };
            }
            newState.isDirty = false;
            newState.writeSeq = 0;
            newState.status = 'ready';
            //NetworkStatus.waiting = false;
            newState.isNew = !!action.isNew;
            /*            } else {
                            const values = action.values;
                            newState = {
                                initialValues: values,
                                values: JSON.parse(JSON.stringify(values)),
                                status: 'ready',
                                isDirty: false,
                                isNew: !!action.isNew
                            };
                        }*/
            break;
        case DiaFormActions.STATUS_SET:
            newState = set(state, 'status', action.value);
            break;
        case DiaFormActions.VALUE_SET:
            const name = action.name ? 'values.' + action.name : 'values';
            if (typeof action.value == 'function') {
                newState = set(state, name, action.value(get(state, name)));
            } else {
                newState = set(state, name, action.value);
            }
            // mark dirty immediately; the relationship-aware comparison runs on the
            // next VERIFY_DIRTY (debounced) or UPDATE, not on every write
            newState.isDirty = true;
            newState.writeSeq = state.writeSeq + 1;
            break;
        case DiaFormActions.TRANSIENT_SET:
            const tName = action.name ? 'transientValues.' + action.name : 'transientValues';
            if (typeof action.value == 'function') {
                newState = set(state, tName, action.value(get(state, tName)));
            } else {
                newState = set(state, tName, action.value);
            }
            break;
        case DiaFormActions.VERIFY_DIRTY:
            // debounced verification after local writes: compare on the current
            // state (synchronous, in the reducer) and clear the write marker
            return {
                ...state,
                isDirty: computeDirty(state),
                writeSeq: 0
            };
        case DiaFormActions.ERRORS_SET:
            newState = set(state, 'errors', action.value);
            break;
        default:
            return state;
    }
    return newState;
}

interface YupSchema {
    validate: (any, Object) => Promise<unknown>
}

interface DiaFormProps<Type,TransientType=never> {
    inputValues?: Type | null,
    isNew?: boolean,
    initializer?: (values: DiaFormState<Type>) => DiaFormState<Type>,
    transientValues?:TransientType,
    validationSchema?: YupSchema,
    relationships?:DiaFormRelationships,
    onSubmit?: (DiaFormSubmitCallbackProps) => Promise<void>,
    onFailure?: (any) => void,
    loaded: boolean,
    failure?: boolean,
    blankValues?: () => any,
    reinitialize?: boolean,
}

export type DiaFormInitializer<Type> = (values: DiaFormState<Type>, Type) => DiaFormState<Type>;

interface DiaFormValue<Type,TransientType=never> {
    values: Type,
    initialize: (rawInputValues: any, isNew: boolean) => void,
    initialValues: Type | DiaFormInitializer<Type> | null,
    transientValues?:TransientType,
    updateInitialValues: (values: Type, name) => void,
    hasFailed: () => void,
    setTransient: (name: string, value: any) => void,
    setValue: (name: string, value: any) => void,
    onChange: (evt) => void,
    isDirty: boolean,
    errors: any,
    handleSubmit: (evt?: any) => void,
    dispatch: React.Dispatch<DiaFormAction<Type>>,
    status: 'ready' | 'loading' | 'validating' | 'submitting' | 'submit-failed',
}

const useDiaForm = <Type,TransientType>({
                              inputValues, isNew = false, initializer, validationSchema, onSubmit, onFailure,relationships,
                              loaded,transientValues
                          }: DiaFormProps<Type,TransientType>): DiaFormValue<Type,TransientType> => {
    const args: DiaFormState<Type,TransientType> = {
        initialValues: null,
        transientValues:transientValues,
        status: 'loading',
        isDirty: false,
        relationships:relationships,
        isNew: isNew,
        values: inputValues,
        writeSeq: 0
    };
    const [store, dispatch] = React.useReducer(reducer, args);
    const v: any = inputValues; // hack to avoid issues
    useEffect(() => {
        if (!inputValues) return;
        if ((inputValues as any)?.id) return;
        // storing an array - merge if there's data, otherwise overwrite
        if (!store.initialValues) {
            dispatch({
                type: DiaFormActions.INITIALIZE,
                inputValues: inputValues,
                initializer: initializer,
                relationships:relationships,
                isNew: isNew,
                transientValues:transientValues
            });
        } else {
            // otherwise attempt to merge the update
            dispatch({
                type: DiaFormActions.UPDATE,
                values: inputValues,
                name: ''
            });
        }
    }, [loaded]);


    useEffect(() => {
        if (!v) return;
        // storing an item
        if(isNew) {
            if(status!='ready') {
                dispatch({
                    type: DiaFormActions.STATUS_SET,
                    value: 'ready'
                });
            }
            if(!store.isNew) {
                dispatch({
                    type: DiaFormActions.INITIALIZE,
                    inputValues: inputValues,
                    initializer: initializer,
                    relationships:relationships,
                    isNew: true,
                    transientValues:transientValues
                });
            }

            return;
        }
        if (v?.id != (store.initialValues as any)?.id) {
            dispatch({
                type: DiaFormActions.INITIALIZE,
                inputValues: inputValues,
                initializer: initializer,
                relationships:relationships,
                isNew: false,
                transientValues:transientValues
            });
        } else {
            // otherwise attempt to merge the update
            dispatch({
                type: DiaFormActions.UPDATE,
                values: inputValues,
                name: ''
            });
        }
    }, [inputValues, (inputValues as any)?.id,isNew]);

    useEffect(() => {
        switch (store.status) {
            case 'submit-failed':
                dispatch({type: DiaFormActions.STATUS_SET, value: 'ready'});
                return;
        }
    }, [store.status]);

    // debounced dirty verification: ~500 ms after the last write (writeSeq > 0)
    // dispatch VERIFY_DIRTY, which runs the relationship-aware comparison on the
    // current state inside the reducer. The pending timer is reset on every
    // write, cleared when the write marker is cleared (INITIALIZE / UPDATE /
    // VERIFY_DIRTY) and cleared on unmount.
    useEffect(() => {
        if (store.writeSeq === 0) return;
        const timer = setTimeout(() => {
            dispatch({type: DiaFormActions.VERIFY_DIRTY});
        }, 500);
        return () => clearTimeout(timer);
    }, [store.writeSeq]);

    // write-only post-save reset: on a successful save that included a
    // write-only field, reset it back to its blank read value ('') so the form
    // does not stay dirty; on a failed save the value is preserved.
    const submitInFlight = useRef(false);
    useEffect(() => {
        const st = store.status;
        if (st === 'submitting') {
            submitInFlight.current = true;
            return;
        }
        if (st === 'submit-failed') {
            // preserve the write-only value on failure
            submitInFlight.current = false;
            return;
        }
        if (st === 'ready' && submitInFlight.current) {
            writeOnlyFields(relationships).forEach((name) => {
                dispatch({type: DiaFormActions.VALUE_SET, name, value: ''});
            });
            submitInFlight.current = false;
        }
    }, [store.status, relationships]);

    const { values, errors, isDirty, status} = store;

    const onChange = useCallback((evt) => {
        dispatch({
            type: DiaFormActions.VALUE_SET,
            value: evt.target.type === 'checkbox' ? (!!evt.target.checked) : evt.target.value,
            name: evt.target.name
        });
    }, []);
    const setValue = useCallback((name, value) => {
        dispatch({type: DiaFormActions.VALUE_SET, value: value, name: name});
    }, []);
    const setTransient = useCallback((name, value) => {
        dispatch({type: DiaFormActions.TRANSIENT_SET, value: value, name: name});
    }, []);
    const hasFailed = useCallback(() => {
        dispatch({type: DiaFormActions.STATUS_SET, value: 'submit-failed'});
    }, []);
    const hasCompleted = useCallback((value) => {
        // not normally needed
        dispatch({type: DiaFormActions.STATUS_SET, value: 'ready'});
    }, []);

    // this action is also rarely needed (campaigns->poster bookings)
    const updateInitialValues = useCallback((values, name) => {
        dispatch({type: DiaFormActions.UPDATE, values: values, name: name})
    }, []);

    // this action should never be needed
    const initialize = useCallback((rawInputValues, isNew) => {
        dispatch({
            type: DiaFormActions.INITIALIZE,
            initializer: initializer,
            rawValues: rawInputValues,
            isNew: isNew
        });
    }, [inputValues, initializer]);

    useEffect(() => {
        if (store.status === 'validating') {
            validationSchema.validate(store.values, {abortEarly: false}).then(() => {
                if (store.errors) {
                    dispatch({type: DiaFormActions.ERRORS_SET, value: null});
                    dispatch({type: DiaFormActions.STATUS_SET, value: 'submit-failed'});
                    if (onFailure) {
                        onFailure(null);
                    }
                }
                dispatch({type: DiaFormActions.STATUS_SET, value: 'submitting'});
            }).catch((errors: any) => {
                const saveErrors = {};
                // yup 0.32 exposes `errors.errors` as an array of message
                // *strings*, so a test's `path` is only reachable on
                // `errors.inner`. Key by the inner errors when they are
                // present — the path is authoritative and is what lets a
                // cross-field test attach its error to a field via
                // createError({ path }) — and fall back to the raw message list
                // otherwise. In both cases a missing path falls back to the
                // first space-separated token of the message (the legacy
                // behaviour, where yup prefixes the message with the field
                // name); for yup-generated messages that token already equals
                // the path, so only custom messages change.
                const inner: any[] = (errors && Array.isArray(errors.inner)) ? errors.inner : [];
                const items: any[] = inner.length
                    ? inner
                    : ((errors && Array.isArray(errors.errors)) ? errors.errors : []);
                items.forEach((e: any) => {
                    const key = (typeof e === 'object' && e && e.path) ? e.path : String(e).split(' ')[0];
                    const message = (typeof e === 'object' && e && e.message) ? e.message : String(e).split(' ').slice(1).join(' ');
                    saveErrors[key] = message;
                });
                dispatch({type: DiaFormActions.ERRORS_SET, value: saveErrors});
                dispatch({type: DiaFormActions.STATUS_SET, value: 'submit-failed'});
                if (onFailure) {
                    onFailure(saveErrors);
                }
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [store.status, onSubmit, onFailure]);

    const handleSubmit = useCallback((evt = null) => {
        if (evt && evt.preventDefault) {
            evt.preventDefault();
        }
        dispatch({type: DiaFormActions.SUBMIT, onSubmit: onSubmit, validationSchema: validationSchema});
    }, [onSubmit, validationSchema]);

    useEffect(() => {
        if (store.status == 'submitting') {
            if(onSubmit) {
                onSubmit({
                    values: store.values,
                    initialValues: store.initialValues,
                    hasFailed: hasFailed,
                    hasCompleted: hasCompleted,
                    updateInitialValues:updateInitialValues,
                    transientValues:store.transientValues,
                    setTransient:setTransient
                });
            }
        }
    }, [store.status]);
    return {
        values: values as Type,
        initialize,
        initialValues: store.initialValues as Type | DiaFormInitializer<Type> | null,
        transientValues: store.transientValues as TransientType,
        updateInitialValues,
        dispatch,
        hasFailed,
        setValue,
        onChange,
        isDirty,
        errors,
        handleSubmit,
        status,
        setTransient
    };
};

export {useDiaForm};